import { 
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  arrayUnion,
  DocumentReference,
  DocumentSnapshot
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { User, Muse } from '../../types';

export class FirebaseService {
  static async createUser(email: string, password: string): Promise<FirebaseUser> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  }

  static async signInUser(email: string, password: string): Promise<FirebaseUser> {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  }

  static async signOutUser(): Promise<void> {
    await signOut(auth);
  }

  static async createUserData(uid: string, userData: User): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    await setDoc(userRef, userData);
  }

  static async getUserData(uid: string): Promise<User | null> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    const docSnap: DocumentSnapshot = await getDoc(userRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as User;
    }
    
    return null;
  }

  static async updateUserData(uid: string, data: Partial<User>): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    await updateDoc(userRef, data);
  }

  static async addMuseToUser(uid: string, muse: Muse): Promise<void> {
    await this.addMusesToUser(uid, [muse]);
  }

  /**
   * Adds multiple muses to a user's collection, preserving duplicates.
   * This enables showing a quantity badge in My Muses based on how many times
   * a muse has been received from pack openings.
   */
  static async addMusesToUser(uid: string, newMuses: Muse[]): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);

    // Use transaction to safely merge muses without downgrading
    const { runTransaction } = await import('firebase/firestore');

    await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const currentUserData = userDoc.data();
      const existingMuses: Muse[] = Array.isArray(currentUserData.muses) ? currentUserData.muses : [];
      // Append new pulls to preserve duplicates
      const updatedMuses: Muse[] = [...existingMuses, ...newMuses];
      transaction.update(userRef, { muses: updatedMuses });
    });
  }

  static async updateSelectedMuse(uid: string, muse: Muse): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    await updateDoc(userRef, { selectedMuse: muse });
  }

  static async updateMuseCash(uid: string, amount: number): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    await updateDoc(userRef, { museCash: amount });
  }

  static async updateStarBalance(uid: string, amount: number): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    await updateDoc(userRef, { stars: amount });
  }

  static async incrementStarBalance(uid: string, increment: number): Promise<void> {
    const userRef: DocumentReference = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data() as User;
      const currentStars = userData.stars || 0;
      await updateDoc(userRef, { stars: currentStars + increment });
    }
  }

  static getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }
} 